/**
 * Splash Screen controller
 *
 * Drives the opaque boot overlay (`#app-splash`) that is painted from raw
 * HTML before the bundle loads. Purely cosmetic — NOT a security control.
 *
 * Every method no-ops when the element is absent (Word plugin, or before the
 * markup exists), so callers never need to guard.
 */
export default class SplashScreen {
  private el: HTMLElement | null;

  constructor(elementId: string = 'app-splash') {
    this.el = document.getElementById(elementId);
  }

  /** True when the splash overlay is present in the DOM. */
  exists(): boolean {
    return this.el !== null;
  }

  /** Fade the overlay out (CSS handles the transition via the class). */
  hide(): void {
    this.el?.classList.add('app-splash--hidden');
  }

  /**
   * Swap the overlay to its error state and wire the Retry button.
   * @param retry - invoked once when the user clicks Retry.
   * @param message - optional text replacing the default error text.
   */
  showError(retry: () => void, message?: string): void {
    if (!this.el) return;

    this.setLoadingVisible(false);

    const text = this.el.querySelector('.app-splash__error p');
    if (message && text) text.textContent = message;

    const retryBtn =
      this.el.querySelector<HTMLButtonElement>('.app-splash__retry');
    const handler = () => {
      retryBtn?.removeEventListener('click', handler);
      this.setLoadingVisible(true);
      retry();
    };
    retryBtn?.addEventListener('click', handler);
  }

  /** Toggle between the loading view (spinner+message) and the error view. */
  private setLoadingVisible(loading: boolean): void {
    if (!this.el) return;
    this.el
      .querySelector('.app-splash__spinner')
      ?.classList.toggle('d-none', !loading);
    this.el
      .querySelector('.app-splash__message')
      ?.classList.toggle('d-none', !loading);
    this.el
      .querySelector('.app-splash__error')
      ?.classList.toggle('d-none', loading);
  }
}
